/**
 * PostHog · narrow route-scoped initialization
 *
 * Prior state: `public/index.html` inlined the PostHog snippet + `posthog.init()`
 * so PostHog + session recording + autocapture + surveys + flags + dead-clicks
 * loaded on EVERY route including `/v2/c/*` (focused customer route). The
 * Marketing V2 Checkpoint 7A.1 audit confirmed that session recording
 * initialized before any consent gate on cold-generic customer traffic and
 * that the V2 design-system experiment engine does not depend on PostHog
 * feature flags.
 *
 * This module moves the SDK snippet + `init` behind a runtime gate.
 *
 * Rules:
 *   1. On the FIRST call to `initPostHogIfEligible(pathname)`:
 *      - If pathname matches `/v2/c/*`, do NOT load the SDK. Set a
 *        session-scoped deferral flag so a later SPA navigation elsewhere
 *        MAY init PostHog, but a customer who never leaves `/v2/c/*` never
 *        downloads any PostHog bytes.
 *      - Otherwise, load the SDK and call `posthog.init` with the EXACT
 *        same configuration the previous inline snippet used. Global
 *        behavior on `/portal/*`, `/book`, `/manage-booking`, etc. is
 *        unchanged.
 *   2. On subsequent calls with a `/v2/c/*` pathname, if PostHog was already
 *      inited earlier in the session, defensively `stopSessionRecording()`
 *      and `opt_out_capturing()` for the duration of that route.
 *   3. On subsequent calls with a non-customer pathname, re-enable capturing
 *      if we had previously opted out for a customer visit.
 *   4. Idempotent. Safe to call from every route change.
 *
 * Privacy Choices integration (2026-08-24):
 *   On PUBLIC CUSTOMER surfaces the shared `Help us understand visits`
 *   preference (see `lib/privacyChoices.js`) governs whether optional
 *   product analytics may run. When the customer sets it Off:
 *     • On a public customer route, PostHog does NOT initialize on
 *       first entry, and if it was already loaded from a prior
 *       internal-route visit, we `stopSessionRecording()`,
 *       `opt_out_capturing()` and `reset()` to clear its persistence.
 *     • On INTERNAL/authenticated routes (`/portal/*`, `/login`,
 *       `/register`) PostHog behavior is preserved unchanged so
 *       legitimate internal product analytics keep working.
 *   When the customer re-enables, PostHog re-initializes normally the
 *   next time an eligible route runs the gate.
 *
 * Non-goals:
 *   - Does NOT create a consent banner.
 *   - Does NOT change any first-party Marketing V2 event pipeline (funnel
 *     events remain via `/api/funnel/event` and V2 handoff tokens).
 *   - Does NOT touch GA4 / Google Ads / GTM setup in `lib/analytics.js`.
 *   - Does NOT modify PostHog behavior on any route outside `/v2/c/*`
 *     when the customer has NOT opted out.
 */
import { isMeasurementEnabled, subscribePrivacyChanges } from "./privacyChoices";

const TEAM_TOKEN = "phc_xAvL2Iq4tFmANRE7kzbKwaSqp1HJjN7x48s3vr0CMjs";
const API_HOST = "https://us.i.posthog.com";

// A pathname is a "focused customer route" if it is `/v2/c/*`. Owner preview
// (`/portal/marketing-v2/preview/:campaignId`) is NOT included because it is
// an internal owner surface and staff-portal analytics is intentional there.
export function isFocusedCustomerRoute(pathname) {
  if (!pathname) return false;
  return pathname.startsWith("/v2/c/");
}

/**
 * A pathname is an "internal Cascade route" — authenticated staff-portal
 * surfaces plus the auth pages that lead to them. The customer's public
 * Privacy Choice does NOT apply here; internal product analytics remain
 * intact (§6 of the Aug 2026 governance directive).
 */
export function isInternalRoute(pathname) {
  if (!pathname) return false;
  return (
    pathname.startsWith("/portal/") ||
    pathname === "/portal" ||
    pathname === "/login" ||
    pathname === "/register"
  );
}

/**
 * A pathname is a "public customer surface" for the purposes of the
 * shared Privacy Choice — anything Cascade-controlled that isn't an
 * internal auth/staff route and isn't the already-suppressed focused
 * customer route.
 */
function isPublicCustomerSurface(pathname) {
  return !isInternalRoute(pathname) && !isFocusedCustomerRoute(pathname);
}

let _initedThisSession = false;
let _optedOutForCustomerRoute = false;
let _optedOutForPrivacyChoice = false;
let _privacySubscribed = false;

// Exactly matches the previous inline snippet's SDK loader. Kept as a function
// so the network request only happens the moment we actually call it.
function loadPostHogSdk() {
  if (window.__posthog_sdk_loaded__) return;
  window.__posthog_sdk_loaded__ = true;
  // Standard PostHog snippet loader — verbatim from the previous
  // `public/index.html`. Downloads `/static/array.js` from PostHog CDN.
  /* eslint-disable */
  !function(e,t){var o,n,p,r;e.__SV||(window.posthog=e,e._i=[],e.init=function(i,s,a){function g(e,t){var o=t.split(".");2==o.length&&(e=e[o[0]],t=o[1]),e[t]=function(){e.push([t].concat(Array.prototype.slice.call(arguments,0)))}}(p=t.createElement("script")).type="text/javascript",p.async=!0,p.src=s.api_host.replace(".i.posthog.com","-assets.i.posthog.com")+"/static/array.js",(r=t.getElementsByTagName("script")[0]).parentNode.insertBefore(p,r);var u=e;for(void 0!==a?u=e[a]=[]:a="posthog",u.people=u.people||[],u.toString=function(e){var t="posthog";return"posthog"!==a&&(t+="."+a),e||(t+=" (stub)"),t},u.people.toString=function(){return u.toString(1)+".people (stub)"},o="init me ws ys ps bs capture je Di ks register register_once register_for_session unregister unregister_for_session Ps getFeatureFlag getFeatureFlagPayload isFeatureEnabled reloadFeatureFlags updateEarlyAccessFeatureEnrollment getEarlyAccessFeatures on onFeatureFlags onSessionId getSurveys getActiveMatchingSurveys renderSurvey canRenderSurvey getNextSurveyStep identify setPersonProperties group resetGroups setPersonPropertiesForFlags resetPersonPropertiesForFlags setGroupPropertiesForFlags resetGroupPropertiesForFlags reset get_distinct_id getGroups get_session_id get_session_replay_url alias set_config startSessionRecording stopSessionRecording sessionRecordingStarted captureException loadToolbar get_property getSessionProperty Es $s createPersonProfile Is opt_in_capturing opt_out_capturing has_opted_in_capturing has_opted_out_capturing clear_opt_in_out_capturing xs debug ts getPageViewId captureTraceFeedback captureTraceMetric".split(" "),n=0;n<o.length;n++)g(u,o[n]);e._i.push([i,s,a])},e.__SV=1)}(window.posthog||[],document);
  /* eslint-enable */
}

// Initializes PostHog with the EXACT same options previously used inline.
// Left intact so behavior on `/portal/*`, `/book`, etc. is unchanged.
function initPostHogNow() {
  if (_initedThisSession) return;
  loadPostHogSdk();
  try {
    window.posthog.init(TEAM_TOKEN, {
      api_host: API_HOST,
      person_profiles: "identified_only",
      // SR-PUBLIC-ERR-001 (owner-authorized 2026-02-08 · Session 7.6):
      // The PostHog session-recorder script (posthog-recorder.js) has
      // been throwing `TypeError: t is not a function` on public
      // customer pages, breaking hydration under the owner's hard
      // invariant "telemetry must fail open." Session recording is
      // NOT load-critical for base analytics. Disable it here; keep
      // event capture + feature flags working normally. If a future
      // PostHog SDK release proves the recorder stable, this flag
      // can be re-enabled — but base analytics must not depend on it.
      disable_session_recording: true,
      capture_performance: false,
    });
    _initedThisSession = true;
  } catch (e) {
    // Non-blocking: analytics must never break a route.
  }
  // Belt-and-suspenders: even if `disable_session_recording` above is
  // ignored by an older loader shim, stop recording explicitly.
  try {
    if (window.posthog && typeof window.posthog.stopSessionRecording === "function") {
      window.posthog.stopSessionRecording();
    }
  } catch (e) {
    // Non-blocking.
  }
}

// Defensive fallback for SPA navigations into `/v2/c/*` when PostHog was
// already loaded from a prior route. Stops recording + opts out until the
// visitor leaves the customer route again.
function suppressForCustomerRoute() {
  if (!_initedThisSession) return;
  if (_optedOutForCustomerRoute) return;
  try {
    if (window.posthog && typeof window.posthog.stopSessionRecording === "function") {
      window.posthog.stopSessionRecording();
    }
    if (window.posthog && typeof window.posthog.opt_out_capturing === "function") {
      window.posthog.opt_out_capturing();
    }
    _optedOutForCustomerRoute = true;
  } catch (e) {
    // Non-blocking.
  }
}

/**
 * Suppress + reset PostHog for the current browser because the customer
 * has explicitly turned "Help us understand visits" Off. Uses supported
 * PostHog APIs only (§5 — no invented deletion). Safe to call whether or
 * not PostHog has been initialized in this session.
 */
function suppressForPrivacyChoiceOff() {
  if (!_initedThisSession) return;
  try {
    if (window.posthog && typeof window.posthog.stopSessionRecording === "function") {
      window.posthog.stopSessionRecording();
    }
  } catch { /* noop */ }
  try {
    if (window.posthog && typeof window.posthog.opt_out_capturing === "function") {
      window.posthog.opt_out_capturing();
    }
  } catch { /* noop */ }
  // `posthog.reset()` clears the distinct_id + any locally stored
  // person/session persistence — the supported PostHog primitive for
  // ending a browsing identity (§5). Optional properties can be lost
  // safely because the customer has just opted out.
  try {
    if (window.posthog && typeof window.posthog.reset === "function") {
      window.posthog.reset(true);
    }
  } catch { /* noop */ }
  _optedOutForPrivacyChoice = true;
  _optedOutForCustomerRoute = false;
}

function restoreAfterCustomerRoute() {
  if (!_initedThisSession) return;
  if (!_optedOutForCustomerRoute) return;
  try {
    if (window.posthog && typeof window.posthog.opt_in_capturing === "function") {
      window.posthog.opt_in_capturing();
    }
    _optedOutForCustomerRoute = false;
  } catch (e) {
    // Non-blocking.
  }
}

/**
 * Route-aware PostHog initialization / suppression.
 *
 * Call once per navigation with the current `location.pathname`.
 *
 * - First call on a customer route → PostHog is NEVER loaded this session.
 * - First call elsewhere → PostHog inits with the historical options — UNLESS
 *   the pathname is a public customer surface AND the shared Privacy
 *   Choice is currently Off, in which case PostHog is NOT initialized.
 * - Later navigations to a customer route → if PostHog is loaded, session
 *   recording is stopped and capturing is opted out until the visitor
 *   leaves the customer route.
 * - Later navigations elsewhere → capturing is opted back in, EXCEPT when
 *   the pathname is a public customer surface and the Privacy Choice is
 *   Off (in which case we keep PostHog suppressed).
 * - Internal routes (`/portal/*`, `/login`, `/register`) ignore the public
 *   Privacy Choice — internal product analytics remain intact.
 */
export function initPostHogIfEligible(pathname) {
  if (typeof window === "undefined") return;

  // One-time subscription so a customer flipping Privacy Choices Off while
  // already on a public customer page immediately suppresses PostHog on
  // that browser (§5).
  if (!_privacySubscribed) {
    _privacySubscribed = true;
    try {
      subscribePrivacyChanges((state) => {
        if (!state.enabled) {
          suppressForPrivacyChoiceOff();
        } else {
          // Re-enable is prospective. If PostHog was already inited this
          // session (from an internal route), opt capturing back in.
          _optedOutForPrivacyChoice = false;
          try {
            if (_initedThisSession && window.posthog && typeof window.posthog.opt_in_capturing === "function") {
              window.posthog.opt_in_capturing();
            }
          } catch { /* noop */ }
        }
      });
    } catch { /* noop */ }
  }

  // Focused customer route (`/v2/c/*`) — PostHog is never loaded there
  // regardless of the Privacy Choice.
  if (isFocusedCustomerRoute(pathname)) {
    if (_initedThisSession) suppressForCustomerRoute();
    return;
  }

  const publicSurface = isPublicCustomerSurface(pathname);
  const measurementOn = isMeasurementEnabled();

  // Public customer surface with Privacy Choice = Off →
  // never initialize, and if a prior internal route inited PostHog,
  // keep it suppressed while the customer stays on this browser.
  if (publicSurface && !measurementOn) {
    if (_initedThisSession) {
      suppressForPrivacyChoiceOff();
    }
    return;
  }

  // Otherwise, follow the historical init/restore behavior.
  if (!_initedThisSession) {
    initPostHogNow();
  } else if (_optedOutForCustomerRoute) {
    restoreAfterCustomerRoute();
  } else if (_optedOutForPrivacyChoice && !publicSurface) {
    // Left the public surface — internal routes get their analytics back.
    try {
      if (window.posthog && typeof window.posthog.opt_in_capturing === "function") {
        window.posthog.opt_in_capturing();
      }
    } catch { /* noop */ }
    _optedOutForPrivacyChoice = false;
  }
}

// Exported for tests + evidence.
export function _debugState() {
  return {
    inited: _initedThisSession,
    opted_out_for_customer_route: _optedOutForCustomerRoute,
    opted_out_for_privacy_choice: _optedOutForPrivacyChoice,
    sdk_loaded: !!window.__posthog_sdk_loaded__,
    posthog_global_present: !!window.posthog,
  };
}
